import TransportTimeline from "@components/common/Timeline/TransportTimeline";
import { Icon, Tag } from "@thetreep/ui";
import {
  Journey,
  LocalDateTime,
  Status,
  Transportation,
  Travel,
} from "@gen/travel/v1/common_pb";
import { priceOfJourney } from "@services/travel";
import { useTranslation } from "react-i18next";
import i18n from "@libs/i18n";
import { localDateTimeToDate } from "@utils/date";
import { formatStatus } from "@utils/status";

type JourneyHeaderProps = {
  travel: Travel;
  journey: Journey;
  showStatus?: boolean;
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
};

export default function JourneyHeader({
  travel,
  journey,
  collapsed,
  setCollapsed,
  showStatus,
}: JourneyHeaderProps) {
  const { t } = useTranslation();

  function displayDate(date: LocalDateTime | undefined) {
    if (!date) return "";
    return Intl.DateTimeFormat(i18n.language, {
      day: "numeric",
      month: "numeric",
      year: "numeric",
    }).format(localDateTimeToDate(date));
  }

  function getType() {
    return journey.segments[0]?.transport?.type;
  }

  function getStatusName(status: number): string {
    switch (status) {
      case Status.UNSPECIFIED:
        return "STATUS_UNSPECIFIED";
      case Status.PRE_BOOKED:
        return "STATUS_PRE_BOOKED";
      case Status.BOOKING_IN_PROGRESS:
        return "STATUS_BOOKED";
      case Status.UPCOMING:
        return "STATUS_UPCOMING";
      case Status.IN_PROGRESS:
        return "STATUS_IN_PROGRESS";
      case Status.COMPLETED:
        return "STATUS_COMPLETED";
      case Status.CANCELLED:
        return "STATUS_CANCELLED";
      default:
        return "STATUS_UNKNOWN";
    }
  }

  function getPrice() {
    const price = priceOfJourney(travel, journey);
    if (price === 0) {
      return "";
    }
    return price / 100 + " €";
  }

  const status = formatStatus(getStatusName(travel.status), t);

  return (
    <div
      className={`cursor-pointer text-lg flex relative justify-between gap-6 flex-col md:flex-row md:items-center md:gap-2 md:h-16`}
      onClick={() => setCollapsed(!collapsed)}
    >
      <div className="flex items-center gap-4">
        {showStatus && (
          <div className="[&_.tagTravel]:whitespace-nowrap">
            <Tag
              className="tagTravel"
              color={status.color}
              bg={status.bg}
              size="small"
              text={status.name}
            />
          </div>
        )}
        <Icon
          size={32}
          color="#3079F2"
          src={
            getType() === Transportation.TRAIN
              ? "train_4_line"
              : "airplane_line"
          }
        />

        <div className="flex items-center gap-1">
          <span className="text-base text-text-strong">
            {displayDate(journey.schedule?.departure)}
          </span>
        </div>
      </div>
      <TransportTimeline showDuration={false} journey={journey} />

      <div className="flex items-center w-full lg:w-[200px] justify-between">
        <div className="flex items-center gap-1 text-base text-text-strong">
          {travel.passengers.length}
          <Icon size={18} color="#282828" src="user_4_fill" />
        </div>
        <div className="flex items-center font-bold text-base text-text-strong">
          {getPrice()}
        </div>
        <div className={`${collapsed ? "rotate-180" : ""}`}>
          <Icon src="up_line" />
        </div>
      </div>
    </div>
  );
}
